// Server-side PDF export — POSTs self-contained HTML to aica
// (/document/pdf/render) which invokes a headless-Chromium Lambda and relays the PDF.
//
// Why: html2canvas rasterises at screen resolution, producing blurry text at
// high zoom and inflated file sizes. Headless Chrome renders the same HTML the
// browser paints, producing compact, text-selectable vector PDFs.
//
// How it works:
//   1. Clone the editor content through the same pre-processing pipeline used
//      by exportToPdfPrint: strip chrome, normalise table columns, trim empty
//      trailing blocks, wait for images.
//   2. Collect the FULL host CSS (every <style> + same-origin <link>) so the PDF
//      is pixel-identical to the editor — NOT a selector-pruned subset, which
//      silently drops layout/variable rules and breaks fidelity.
//   3. Inline every @font-face as a data: URI so brand fonts render on the
//      Lambda (which blocks network during render, so fonts can't be fetched).
//   4. gzip the self-contained HTML and POST { htmlGz, fileName } to aica.
//   5. aica returns binary application/pdf; axios delivers it as a Blob.
//   6. Falls back to `exportEditorToPdfRaster` if the request fails,
//      so the user always gets a PDF.

import axios from 'axios';
import { aicaBaseUrl } from '../../../utils/axios-interceptor';
import { exportEditorToPdf as exportEditorToPdfRaster } from './exportToPdf';
import {
    waitForImages,
    inlineImagesToDataUris,
    stripInteractiveChrome,
    normalizeTableColumnsForPrint,
    trimTrailingEmptyBlocks,
    trimTrailingWhitespace,
} from './exportHelpers';

interface ExportToPdfServerOptions {
    target: HTMLElement;
    filename?: string;
    /** When true, return the Blob instead of triggering a download. */
    returnBlob?: boolean;
}

// Shared DOM pre-processing helpers (waitForImages, stripInteractiveChrome,
// normalizeTableColumnsForPrint, trimTrailingEmptyBlocks, inlineImagesToDataUris)
// now live in ./exportHelpers and are imported above — shared with exportToPdfPrint.

// Collect the FULL host CSS: every <style> as source text plus same-origin
// <link rel=stylesheet> fetched and inlined. We deliberately do NOT prune by
// selector — selector-pruning silently drops load-bearing rules (selectors
// anchored on the editor's own container class, `:root` custom-property
// definitions, ancestor base typography) and visibly wrecks layout/fonts. The
// whole stylesheet is sent instead; it's gzipped on the wire (see gzipToBase64),
// so the payload stays small while the PDF stays pixel-identical to the editor.
//
// External stylesheets (CDNs, Google Fonts) are dropped — the Lambda can't fetch
// them and they aren't needed once @font-face is inlined (see inlineFonts).
async function collectFullCss(): Promise<string> {
    const nodes = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'));
    const parts = await Promise.all(
        nodes.map(async (n) => {
            if (n.tagName === 'LINK') {
                const href = (n as HTMLLinkElement).href || '';
                const sameOrigin = href.startsWith(window.location.origin) || href.startsWith('/');
                if (!sameOrigin) return '';
                try {
                    const css = await fetch(href).then((r) => (r.ok ? r.text() : ''));
                    return css ? `<style>${css}</style>` : '';
                } catch {
                    return '';
                }
            }
            return (n as HTMLElement).outerHTML;
        }),
    );
    return parts.filter(Boolean).join('\n');
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const CHUNK = 0x8000; // chunk to avoid call-stack limits on large buffers
    for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + CHUNK)));
    }
    return btoa(binary);
}

// Inline every @font-face as a data: URI so brand fonts (e.g. Gilroy) actually
// render on the Lambda. The Lambda aborts all network fetches during render (so
// an unreachable/slow font URL can never stall it), which means fonts MUST
// travel inline or text silently falls back to a system serif. Font files are
// same-origin in dev (webpack dev server) and prod (app origin), so the browser
// can fetch them here and embed the bytes.
async function inlineFonts(): Promise<string> {
    const faceRules: CSSFontFaceRule[] = [];
    for (const sheet of Array.from(document.styleSheets)) {
        let rules: CSSRuleList | null = null;
        try {
            rules = sheet.cssRules;
        } catch {
            continue; // cross-origin sheet — not readable
        }
        if (!rules) continue;
        for (const rule of Array.from(rules)) {
            if (rule instanceof CSSFontFaceRule) faceRules.push(rule);
        }
    }

    const resolveUrl = (raw: string): string => {
        if (raw.startsWith('http') || raw.startsWith('data:')) return raw;
        if (raw.startsWith('//')) return window.location.protocol + raw;
        return new URL(raw, window.location.href).href;
    };

    const faces = await Promise.all(
        faceRules.map(async (rule) => {
            const src = rule.style.getPropertyValue('src');
            const match = src.match(/url\(\s*["']?([^)"']+\.(?:woff2|woff|ttf|otf))["']?\s*\)/i);
            if (!match) return '';
            const family = rule.style.getPropertyValue('font-family');
            const weight = rule.style.getPropertyValue('font-weight') || 'normal';
            const fontStyle = rule.style.getPropertyValue('font-style') || 'normal';
            try {
                const buf = await fetch(resolveUrl(match[1])).then((r) => (r.ok ? r.arrayBuffer() : null));
                if (!buf) return '';
                const ext = match[1].split(/[?#]/)[0].split('.').pop()!.toLowerCase();
                const mime = ext === 'woff2' ? 'font/woff2' : ext === 'woff' ? 'font/woff' : ext === 'otf' ? 'font/otf' : 'font/ttf';
                const fmt = ext === 'woff2' ? 'woff2' : ext === 'woff' ? 'woff' : ext === 'otf' ? 'opentype' : 'truetype';
                return `@font-face{font-family:${family};font-style:${fontStyle};font-weight:${weight};font-display:swap;src:url(data:${mime};base64,${arrayBufferToBase64(
                    buf,
                )}) format("${fmt}")}`;
            } catch {
                return '';
            }
        }),
    );

    const block = faces.filter(Boolean).join('\n');
    return block ? `<style>${block}</style>` : '';
}

// gzip the payload in the browser so the full stylesheet + inlined fonts fit well
// under the Lambda Function URL's 6 MB request limit (≈5.7 MB raw → ≈1.7 MB).
// CompressionStream ships in all Chromium-based browsers, which this app targets.
async function gzipToBase64(text: string): Promise<string> {
    const CompressionStreamCtor = (window as unknown as { CompressionStream?: any }).CompressionStream;
    // `pipeThrough` isn't in this TS lib's ReadableStream type but exists at
    // runtime in all Chromium browsers — cast through `any`.
    const blobStream = new Blob([text]).stream() as any;
    const stream = blobStream.pipeThrough(new CompressionStreamCtor('gzip'));
    const buffer = await new Response(stream).arrayBuffer();
    return arrayBufferToBase64(buffer);
}

// ── HTML payload builder ──────────────────────────────────────────────────────

async function buildSelfContainedHtml(contentEl: HTMLElement, filename: string): Promise<string> {
    // Clone and pre-process exactly as exportToPdfPrint does.
    const clone = contentEl.cloneNode(true) as HTMLElement;
    stripInteractiveChrome(clone);
    trimTrailingEmptyBlocks(clone);
    trimTrailingWhitespace(clone);
    normalizeTableColumnsForPrint(clone);
    // Mark the editor root so the wrapper can strip ONLY its editor card chrome
    // (border / radius / shadow / outline) and editing min-height. Scoped to the
    // root so tables, cells and everything inside are left exactly as the app's
    // own CSS renders them — DO NOT add table/cell/`body > *` rules here.
    clone.setAttribute('data-pdf-export-root', '');

    // Embed every image as a data: URI BEFORE serialising. ImageElement renders a
    // docId as a presigned https:// S3 URL; the Lambda aborts all non-data: network
    // fetches during render, so an un-inlined <img> renders blank. Done on the
    // detached clone so the live editor is untouched.
    await inlineImagesToDataUris(clone);

    // Pacifico (the EL signature font) is now self-hosted same-origin in index.scss,
    // so inlineFonts() reads its @font-face and embeds the bytes alongside Gilroy —
    // no special-case Google fetch needed.
    const [hostStyles, fontStyles] = await Promise.all([collectFullCss(), inlineFonts()]);
    const bodyContent = clone.outerHTML;
    const safeTitle = filename.replace(/[<>&"]/g, '');

    // Two targeted, layout-neutral print corrections only:
    //   1. [data-pdf-export-root] — remove the editor's card border + editing
    //      min-height (the stray top/left border in the PDF).
    //   2. @page box + print colour fidelity.
    // The blank trailing page is handled in the DOM by trimTrailingWhitespace.
    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${safeTitle}</title>
    ${hostStyles}
    ${fontStyles}
    <style>
      @page { size: A3 portrait; margin: 0.6in; }
      html, body { margin: 0; padding: 0; background: #ffffff; }
      [data-pdf-export-root] { border: none !important; border-radius: 0 !important; box-shadow: none !important; outline: none !important; min-height: 0 !important; }
      /* normalizeTableColumnsForPrint() already made every table fit its
         container (% columns + the app's width:100%), so the live editor's
         horizontal scroll box (.tableScroll, RECUR-26175) has no overflow here.
         Neutralise it anyway so the static print HTML can never render a clipped
         table or a scrollbar. Layout-neutral — not a table/cell restyle. */
      [class*="tableScroll"] { overflow: visible !important; }
      *, *::before, *::after { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
    </style>
  </head>
  <body>${bodyContent}</body>
</html>`;
}

// ── aica fetch ─────────────────────────────────────────────────────────────────

async function fetchPdfFromAica(htmlGz: string, fileName: string): Promise<Blob> {
    const response = await axios({
        baseURL: aicaBaseUrl,
        url: '/document/pdf/render',
        method: 'POST',
        data: { htmlGz, fileName },
        responseType: 'blob',
    });
    return new Blob([response.data], { type: 'application/pdf' });
}

function triggerDownload(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoke after a short delay so the download has time to start.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// ── Public API ────────────────────────────────────────────────────────────────

export async function exportEditorToPdfServer({
    target,
    filename = 'document',
    returnBlob = false,
}: ExportToPdfServerOptions): Promise<Blob | void> {
    // Locate the editor content root, mirroring exportToPdfPrint.
    const contentEl =
        (target.querySelector('[data-testid="plate-editor-content"]') as HTMLElement | null) ??
        (target.querySelector('[contenteditable]') as HTMLElement | null) ??
        target;

    // Wait for images before serialising so src attributes are fully resolved.
    await waitForImages(contentEl);

    let blob: Blob;
    try {
        const html = await buildSelfContainedHtml(contentEl, filename);
        const htmlGz = await gzipToBase64(html);
        blob = await fetchPdfFromAica(htmlGz, filename);
    } catch (err) {
        const e = err as { response?: { status?: number }; code?: string; message?: string };
        console.error(
            '[exportEditorToPdfServer] aica PDF request failed, falling back to raster:',
            e?.response?.status ?? e?.code ?? e?.message ?? 'unknown error',
        );
        return exportEditorToPdfRaster({ target, filename, returnBlob }) as Promise<Blob | void>;
    }

    if (returnBlob) return blob;
    triggerDownload(blob, filename);
}
